// src/components/landing/PlanosBlock.tsx
// ✅ ATUALIZADO: preço livre (priceLine1 + priceLine2) + bullets para todos os slots

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowRight, Check, Star } from 'lucide-react';
import './PlanosBlock.css';

interface SlotConfig {
    productId: string;
    planIndex: number;
    customTitle: string;
    customDescription: string;
    customBullets: string[];
    showBadge: boolean;
    badgeText: string;
    ctaText: string;
    footerNote: string;
    // ✅ NOVO: preço totalmente livre
    priceLine1: string;
    priceLine2: string;
    product?: {
        _id: string;
        title: string;
        description: string;
        type: string;
        plans: { durationMonths: number | null; priceCents: number }[];
    };
}

interface LandingConfig {
    sectionTitle: string;
    sectionSubtitle: string;
    slotMain: SlotConfig;
    slotTopRight: SlotConfig;
    slotBottomRight: SlotConfig;
}

const DEFAULT_BULLETS = [
    'Todos os flashcards e atualizações futuras',
    'Todos os modos de estudo disponíveis',
    'Banco de questões completo',
    'Apostilas interativas integradas',
    'Métricas avançadas de desempenho',
    'Acesso aos grupos de estudo',
    'Suporte contínuo do Assistente Graia',
];

const PlanosBlock: React.FC = () => {
    const navigate = useNavigate();
    const [config, setConfig] = useState<LandingConfig | null>(null);
    const [openCombo, setOpenCombo] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold: 0.1 }
        );
        if (sectionRef.current) obs.observe(sectionRef.current);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch('/api/landing/planos-config');
                if (res.ok) {
                    const data = await res.json();
                    if (data.config) setConfig(data.config);
                }
            } catch { /* usa fallback */ }
        };
        load();
    }, []);

    const getTitle = (slot: SlotConfig | undefined, fallback: string) =>
        slot?.customTitle || slot?.product?.title || fallback;

    const getDesc = (slot: SlotConfig | undefined, fallback: string) =>
        slot?.customDescription || slot?.product?.description || fallback;

    const getBullets = (slot: SlotConfig | undefined) =>
        (slot?.customBullets?.length ? slot.customBullets : DEFAULT_BULLETS);

    const handleCta = (slot: SlotConfig | undefined) => {
        if (!slot?.productId) { navigate('/marketplace'); return; }
        navigate(`/marketplace/${slot.productId}`);
    };

    return (
        <section
            ref={sectionRef}
            className={`ldgpr-planos ${visible ? 'ldgpr-planos--visible' : ''}`}
        >
            <div className="ldgpr-planos-container">

                {/* Cabeçalho */}
                <div className="ldgpr-planos-header">
                    <p className="ldgpr-planos-label">Planos & Acesso</p>
                    <h2 className="ldgpr-planos-headline">
                        {config?.sectionTitle ?? 'Escolha como quer avançar na graduação.'}
                    </h2>
                    <p className="ldgpr-planos-sub">
                        {config?.sectionSubtitle ?? 'Acesso total à plataforma ou foco cirúrgico em uma etapa específica.'}
                    </p>
                </div>

                {/* Grid 2 colunas */}
                <div className="ldgpr-planos-grid">

                    {/* ── COLUNA 1: CARD PRINCIPAL ── */}
                    <div className="ldgpr-plan-full">
                        {(config?.slotMain?.showBadge ?? true) && (
                            <span className="ldgpr-plan-full-badge">
                                <Star size={10} fill="currentColor" />
                                {config?.slotMain?.badgeText ?? 'Recomendado para constância'}
                            </span>
                        )}

                        <p className="ldgpr-plan-full-title">
                            {getTitle(config?.slotMain, 'Plano Completo')}
                        </p>
                        <p className="ldgpr-plan-full-desc">
                            {getDesc(config?.slotMain, 'A estrutura integral do GRAIA para organizar todo o seu ciclo de estudo.')}
                        </p>

                        <ul className="ldgpr-plan-full-list">
                            {getBullets(config?.slotMain).map((item, i) => (
                                <li key={i}>
                                    <Check size={13} strokeWidth={2.5} />
                                    {item}
                                </li>
                            ))}
                        </ul>

                        <p className="ldgpr-plan-full-anchor">
                            Economicamente mais vantajoso do que adquirir módulos separadamente.
                        </p>

                        {/* ✅ PREÇO LIVRE */}
                        <div className="ldgpr-plan-full-price-block">
                            {config?.slotMain?.priceLine1 ? (
                                <span className="ldgpr-plan-price-main">{config.slotMain.priceLine1}</span>
                            ) : (
                                <span className="ldgpr-plan-price-main">R$ --</span>
                            )}
                            {config?.slotMain?.priceLine2 && (
                                <span className="ldgpr-plan-price-secondary">{config.slotMain.priceLine2}</span>
                            )}
                        </div>

                        {config?.slotMain?.footerNote && (
                            <p className="ldgpr-plan-price-note">{config.slotMain.footerNote}</p>
                        )}

                        <button className="ldgpr-plan-full-cta" onClick={() => handleCta(config?.slotMain)}>
                            {config?.slotMain?.ctaText ?? 'Começar com Acesso Total'}
                            <ArrowRight size={15} strokeWidth={2} />
                        </button>
                    </div>

                    {/* ── COLUNA 2: DIREITA ── */}
                    <div className="ldgpr-plan-combos">

                        {/* Card topo direita */}
                        <div className="ldgpr-plan-small-card">
                            {config?.slotTopRight?.showBadge && (
                                <span className="ldgpr-plan-small-badge">
                                    <Star size={9} fill="currentColor" />
                                    {config.slotTopRight.badgeText}
                                </span>
                            )}

                            <p className="ldgpr-plan-small-title">
                                {getTitle(config?.slotTopRight, 'Plano Mensal')}
                            </p>
                            <p className="ldgpr-plan-small-desc">
                                {getDesc(config?.slotTopRight, '')}
                            </p>

                            {/* ✅ Bullets do card topo direita */}
                            {(config?.slotTopRight?.customBullets?.length ?? 0) > 0 && (
                                <ul className="ldgpr-plan-small-list">
                                    {config!.slotTopRight.customBullets.map((item, i) => (
                                        <li key={i}>
                                            <Check size={11} strokeWidth={2.5} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {/* ✅ PREÇO LIVRE */}
                            <div className="ldgpr-plan-small-price-block">
                                {config?.slotTopRight?.priceLine1 ? (
                                    <span className="ldgpr-plan-small-price-main">{config.slotTopRight.priceLine1}</span>
                                ) : (
                                    <span className="ldgpr-plan-small-price-main">R$ --</span>
                                )}
                                {config?.slotTopRight?.priceLine2 && (
                                    <span className="ldgpr-plan-small-price-secondary">{config.slotTopRight.priceLine2}</span>
                                )}
                            </div>

                            {config?.slotTopRight?.footerNote && (
                                <p className="ldgpr-plan-small-note">{config.slotTopRight.footerNote}</p>
                            )}

                            <button
                                className="ldgpr-plan-small-cta"
                                onClick={() => handleCta(config?.slotTopRight)}
                            >
                                {config?.slotTopRight?.ctaText ?? 'Ver detalhes'}
                                <ArrowRight size={13} strokeWidth={2} />
                            </button>
                        </div>

                        {/* Card base direita — combo */}
                        <div className="ldgpr-combos-list">
                            {config?.slotBottomRight && (
                                <div className={`ldgpr-combo ${openCombo ? 'ldgpr-combo--open' : ''}`}>
                                    <button
                                        className="ldgpr-combo-trigger"
                                        onClick={() => setOpenCombo(v => !v)}
                                    >
                                        <div className="ldgpr-combo-trigger-left">
                                            <span className="ldgpr-combo-name">
                                                {getTitle(config.slotBottomRight, 'Combo')}
                                            </span>
                                            <span className="ldgpr-combo-desc">
                                                {getDesc(config.slotBottomRight, '')}
                                            </span>
                                        </div>
                                        <div className="ldgpr-combo-trigger-right">
                                            {/* ✅ Preço compacto no trigger */}
                                            {config.slotBottomRight.priceLine1 && (
                                                <div className="ldgpr-combo-price-block">
                                                    <span className="ldgpr-combo-price">{config.slotBottomRight.priceLine1}</span>
                                                    {config.slotBottomRight.priceLine2 && (
                                                        <span className="ldgpr-combo-price-secondary">{config.slotBottomRight.priceLine2}</span>
                                                    )}
                                                </div>
                                            )}
                                            <ChevronDown size={14} className="ldgpr-combo-chevron" strokeWidth={2} />
                                        </div>
                                    </button>
                                    <div className="ldgpr-combo-body">
                                        {/* ✅ Bullets do combo */}
                                        {config.slotBottomRight.customBullets?.length > 0 && (
                                            <ul className="ldgpr-combo-bullets">
                                                {config.slotBottomRight.customBullets.map((item, i) => (
                                                    <li key={i}>
                                                        <Check size={11} strokeWidth={2.5} />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {config.slotBottomRight.footerNote && (
                                            <p className="ldgpr-combo-note">
                                                {config.slotBottomRight.footerNote}
                                            </p>
                                        )}
                                        <button
                                            className="ldgpr-combo-detail-btn"
                                            onClick={() => handleCta(config.slotBottomRight)}
                                        >
                                            {config.slotBottomRight.ctaText ?? 'Ver detalhes do combo'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="ldgpr-combos-footer">
                            <p>Prefere montar seu próprio caminho?</p>
                            <button
                                className="ldgpr-combos-marketplace-btn"
                                onClick={() => navigate('/marketplace')}
                            >
                                Comprar decks individuais
                                <ArrowRight size={13} strokeWidth={2} />
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default PlanosBlock;